/**
 * The words of the dashboard and the monthly report, in one place.
 *
 * Every figure has one name and one definition. The info pop-ups on the page, the
 * "How we count" section of the printed report and the Help page read the same text, so
 * a definition cannot say one thing on screen and another on paper.
 *
 * Writing rules (ASD-STE100, round 3): plain business English, one instruction per sentence,
 * at most 25 words, active voice with a named doer (I2Q, sales, the customer), always "sales"
 * for the people who quote, no long dashes, no internal words (Truth, alias, codes). The one
 * name for the state I2Q runs in is "Shadow mode": never "test run" or "pilot not started".
 *
 * The small helpers at the end word the whole-thread agreement from `/metrics/ops` for the
 * sales view (My area); Summary and the report read `/metrics/executive`.
 */

import type { OpsThreadSplit } from "@/lib/api";
import { countNoun, formatCount, formatPercent } from "@/lib/format";

/** The fixed line under every page title. */
export const TEST_RUN_LINE =
  "Shadow mode: I2Q reads the mail and prepares the draft. I2Q sends nothing to customers and writes nothing to F&O. Sales still reply in Outlook and quote in F&O.";

/** Bases below this are printed as "x of y" with this caption, never as a percentage. */
export const TOO_FEW = "Too few to judge yet";

/** Shown instead of a figure the data cannot give yet (the reason is in the info text). */
export const NOT_MEASURED = "Not measured yet";

/** Shown instead of zeros for figures that start with the pilot. */
export const NOT_STARTED = "Not started";

export const PILOT_NOT_STARTED =
  "Shadow mode: nobody has worked a case in I2Q yet. Figures appear once sales work cases in I2Q.";

export const OPEN_IN_I2Q_NOTE =
  "In shadow mode sales reply in Outlook and quote in F&O. Open means nobody closed the case in I2Q; the customer may have a reply.";

export const SLOW_NOTICE =
  "Working out the figures. This can take up to 30 seconds the first time today.";

/** The name of each figure, used as the tile label and in the glossary. */
export const LABELS = {
  agreement: "Matches what sales quoted",
  precision: "Right when I2Q is sure",
  inquiries: "Inquiries handled",
  autonomy: "Done without a person",
  speed: "Ready within",
  replyTime: "Sales reply time",
  value: "Quotation value",
  status: "Status",
  areas: "Areas against the go-live bar",
  qualityTrend: "Quality over the weeks",
  decisions: "Next steps waiting for a decision",
  ready: "Ready to quote",
  matched: "Customer and items matched",
  needsCheck: "Needs a check",
  customerToConfirm: "Customer to confirm",
  errors: "Errors",
  openByAge: "Open in I2Q by age",
  whatI2qDid: "What I2Q did each week",
  outcomes: "Where inquiries stand",
  perWeek: "Inquiries per week",
  qualityByArea: "Quality by area",
  customerMatching: "Customer named by I2Q",
  pilot: "Pilot",
  mailImport: "Mail import",
  masterData: "Master data",
  switches: "Switches",
  margin: "Margin thresholds",
  learning: "Learning from corrections",
  measured: "Measured",
  estimate: "Estimate",
} as const;

/** What each figure means, in the words of the info pop-ups, the report and Help. */
export const DEFINITIONS = {
  agreement:
    "The headline figure. Of the item lines sales quoted in F&O for these inquiries, the share where I2Q proposed the same item in any mail of the thread. Single mail: the same lines, judged on the one mail I2Q prepared only. Revision mails (the customer asks to change a quotation) will show apart once the quotation dates are filled in. Only quotations found in F&O count.",
  precisionItems:
    "When I2Q marked an item as sure, how often sales quoted that same item. Split or partial quotations count as wrong, so this figure is cautious. The go-live bar is 99.5%. The second figure, with split quotations, also counts a sure item as right when sales quoted it to the same customer within 30 days.",
  precisionCustomer:
    "When I2Q named the customer with certainty, how often it was the customer on the F&O quotation.",
  inquiries:
    "Mails I2Q recognised as an inquiry and prepared. Norican's own replies do not count. Counted the same way as What I2Q did each week; the Operations tab counts by mail and can differ slightly.",
  autonomy:
    "Lines: the share of inquiry lines I2Q matched to an item with certainty. Customer and items matched: the share where the customer was certain and every line matched; this says nothing about quantities or prices. Ready to quote: matched, and every line has a quantity and a current price that passed the price checks, and no rule holds the case. Sales still check the quotation before they send it. Right in F&O: of the ready inquiries with a quotation in F&O, how many matched it completely. The go-live bar is 75% of lines.",
  speed:
    "Time from the arrival of the mail to the prepared case. Only mails read within 3 hours of arrival count, because the older mails came in later in bulk.",
  replyTime:
    "Time from the mail of the customer to the reply of sales in the sent items. Only areas with sent items that I2Q reads count.",
  value:
    "Total of the F&O quotations sales made for inquiries I2Q prepared, in the currency of each company, and the share marked won in F&O. Only inquiries with a quotation found in F&O. Never added up across currencies.",
  status:
    "Running: I2Q reads mail every 5 minutes. Master data: the age of the F&O copy against its limit. Nothing sent: the send block is on in shadow mode.",
  areas:
    "Every area stands on its own; there is no group figure. Meets bar: right when sure at least 99.5% on at least 100 lines, and at least 75% of lines done without a person. Below bar: measured on enough lines, but under one of the bars. Not enough data: fewer than 100 lines, or too few quotations found in F&O. Import problem: the mail import has stopped, so the figures are not current.",
  qualityTrend:
    "Matches what sales quoted, per week, over the whole thread and for the single mail. Weeks with fewer than 30 quotations found in F&O stay as gaps. The current week counts so far.",
  ready:
    "The customer is certain, every line has an item, a quantity and a price that passed the price checks, and no rule holds the case. Sales still check the quotation before they send it.",
  matched:
    "The customer is certain and every line has an item. Quantities and prices may still be missing.",
  needsCheck: "At least one line needs a person to pick or confirm the item.",
  customerToConfirm: "I2Q found items but needs a confirmed customer before it quotes.",
  errors: "Mails I2Q could not read or prepare. A person must look at them.",
  openByAge:
    "Open inquiries in I2Q from the last 60 days, by the day they arrived. Replies without lines of their own count with their first mail, as in the queue. Inquiries open for longer count apart. Closed by I2Q: a reply outside I2Q (mail moved to the done folder, a reply in the thread, or a Norican reply), an I2Q status. Each bar links to the queue with the same area and dates.",
  whatI2qDid:
    "Per week: the inquiries I2Q prepared, the share with a certain customer, the share with every line matched, and the share ready to quote. Ready to quote also needs a quantity and a checked current price on every line. The current week counts so far.",
  outcomes:
    "Where the inquiries of the period stand now. Each count links to the queue with the same set.",
  perWeek:
    "Inquiries per week, split into ready to quote and the rest that needs a person. The current week counts so far.",
  qualityByArea:
    "The quality figures of each area for the period. Found in F&O: the share of inquiries with a quotation found in F&O, the base of every quality figure.",
  measured: "Counted from stored facts, no assumption.",
  estimate: "Contains an assumption, for example minutes per line.",
  pilot:
    "Cases a person opened and closed in I2Q. Starts with the pilot; until then this shows Not started.",
} as const;

/** After the share of ready inquiries on Summary: what "ready" checked (F14). */
export const READY_WITH_CHECKS = "ready to quote, with quantities and current prices checked";

/** The go-live status of an area, in words. */
export const GATE_LABELS = {
  meets: "Meets bar",
  below: "Below bar",
  not_enough: "Not enough data",
  feed_problem: "Import problem",
} as const;

// ─── whole-thread agreement from /metrics/ops (sales view, My area) ──────────

/** An agreement figure below this many compared lines is not shown as a percentage. */
export const MIN_AGREEMENT_LINES = 50;

/**
 * Header tip of the whole-thread figure, the headline agreement since 10 Oct 2026 (Timo's
 * decision): the whole thread first, the single mail beside it, revision mails apart.
 */
export const THREAD_AGREEMENT_TIP = DEFINITIONS.agreement;

/** The sentence shown instead of a percentage measured on too few lines. */
export function tooFewLines(lines: number): string {
  return `${TOO_FEW} (${countNoun(lines, "line")})`;
}

/** The whole-thread figure over all F&O quotations, or the sentence shown instead. */
export function threadValue(split: readonly OpsThreadSplit[] | undefined): string {
  const all = split?.find((s) => s.kind === "all");
  if (!all || all.line_agreement_thread == null || all.lines <= 0) {
    return "No F&O quotations yet";
  }
  if (all.lines < MIN_AGREEMENT_LINES) {
    return tooFewLines(all.lines);
  }
  return formatPercent(all.line_agreement_thread) ?? NOT_MEASURED;
}

/**
 * Under the whole-thread figure: the single-mail figure on the same lines, then the
 * revision mails apart ("Revision mails: 71 quotations, 19% single mail, 45% whole thread").
 */
export function threadCaption(split: readonly OpsThreadSplit[] | undefined): string {
  const all = split?.find((s) => s.kind === "all");
  if (!all || all.lines <= 0) {
    return "Measured on F&O quotations only.";
  }
  const pct = (value: number | null | undefined) => formatPercent(value) ?? NOT_MEASURED;
  const parts = [
    `Single mail ${pct(all.line_agreement_single)} on the same ${countNoun(all.lines, "line")}`,
  ];
  const revision = split?.find((s) => s.kind === "revision");
  if (revision && revision.lines > 0) {
    parts.push(
      `Revision mails: ${countNoun(revision.quotations, "quotation")}, ${pct(
        revision.line_agreement_single,
      )} single mail, ${pct(revision.line_agreement_thread)} whole thread`,
    );
  }
  const fresh = split?.find((s) => s.kind === "new");
  if (fresh && fresh.lines > 0 && revision && revision.lines > 0) {
    parts.push(
      `New inquiries: ${pct(fresh.line_agreement_single)} single mail, ${pct(
        fresh.line_agreement_thread,
      )} whole thread`,
    );
  }
  return `${parts.join(". ")}.`;
}

/** "3,031 lines in 412 quotations", with singular forms. */
export function agreementCaption(lines: number, documents: number): string {
  return `${countNoun(lines, "line")} in ${countNoun(documents, "quotation")}`;
}

/**
 * `part` as a whole-number share of `whole` ("12"), "<1" when a part exists but rounds
 * to 0, or null when `whole` is 0. Printed with a "%" behind it.
 */
export function share(part: number, whole: number): string | null {
  if (whole <= 0) {
    return null;
  }
  const rounded = Math.round((part / whole) * 100);
  return rounded === 0 && part > 0 ? "<1" : formatCount(rounded);
}
